// ADR 8.9.26i §3 — the bar the web never had. ADR 28.9.26e §7 — Android's:
// a large title on the left, a settings gear on the right.
//
// Before 8.9.26i there was **no app-level header at all**. This is the page's
// one `banner` landmark; the two existing `<header>` elements
// (`SharedPlanPage.tsx:145`, `PlanDetailPage.tsx:68`) are scoped inside
// sectioning content and stay generic, so nothing collides.
//
// 28.9.26e retired the account menu and the brand mark. The owner's screenshot
// of the phone is a per-screen `TopAppBar` — the screen's title and a gear —
// so the menu's two entries (install, sign-out) moved to `AccountPage`, which
// the gear opens, and the bar carries no mark. `TopBar`'s `leading` slot stays
// as 15.9.26ah defined it, with no caller.

import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useRouteApps, useRouteBackTo, useRouteTitleKey } from '../app/RouteAnnouncer'
import { gated } from '../app/wasmGate'
import type { HeaderAppsProps } from '../features/apps/sheet/HeaderApps'
import { InstallButton } from '../pwa/InstallButton'
import { TopBar } from '../ui/TopBar'
import { destinationsFor, type Destination } from './nav/destinations'
import { HeaderSwitcher } from './switcher/HeaderSwitcher'

// ADR 6.10.26l D5 — the Apps icon reaches the wasm (`appBarEntries`), and the
// header is on the boot graph, so it comes in through the gate as `ShellBody`'s
// `AppSlot` does, and draws nothing until it resolves.
const HeaderApps = lazy(async () => ({
  default: await gated<HeaderAppsProps, 'HeaderApps'>(() => import('../features/apps/sheet/HeaderApps'), 'HeaderApps'),
}))

/**
 * The **destination's** title — the header's fallback when the route table
 * names none (a table without handles, as the header suite's is).
 *
 * Longest prefix wins, so `/plans/:id` reads the Lessons label; a route under no
 * destination at all (`/verify`) falls back to the first, which is Home. `/`
 * matches only itself: no path starts with `//`.
 */
export function titleKeyFor(pathname: string, items: Destination[]): string | null {
  if (items.length === 0) return null
  let best: Destination | null = null
  for (const item of items) {
    const matches = pathname === item.path || pathname.startsWith(`${item.path}/`)
    if (matches && (best === null || item.path.length > best.path.length)) best = item
  }
  return (best ?? items[0]).labelKey
}

/**
 * One bar: the route's title, a back arrow where the route names one, the gear.
 *
 * ADR 15.9.26ah §5 — drawn on `TopBar`, so the title is the page's `<h1>`.
 * `TopBar`'s root is a `<header>` and a `<header>` may not nest inside
 * another, so the outer element is a `<div>` carrying the test id and the
 * strip's border; the `banner` landmark is `TopBar`'s.
 *
 * ADR 28.9.26e §7 — the title is the **route's** own (`handle.titleKey`), as
 * Android's per-screen `TopAppBar` is, so `/account` reads Account rather than
 * the first destination's name. The arrow is `handle.backTo`: the review route
 * is immersive (no nav, §8), and this is its way out. The gear is Android's —
 * same key (`settings_title`), same place.
 *
 * ADR 3.10.26f §3 — the trailing slot is `HeaderSwitcher`: the plain gear for
 * a learner, the box of doors (Settings, Developer, Apps & connections, with
 * pins) for a visitor the console admits. It replaced 30.9.26s §4's
 * `ConsoleSwitch` and the gear, and stays inside `TopBar`, never a new frame
 * sibling (8.9.26i's `<Outlet>` invariant).
 *
 * ADR 6.10.26a §1 — `InstallButton` sits before it, inside `TopBar` for the
 * same reason; it renders nothing when there is nothing to install.
 *
 * ADR 6.10.26l D2 — `HeaderApps` sits before `InstallButton` on a route whose
 * handle carries `apps`, so `HeaderSwitcher` stays the trailing item.
 */
export function Header() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  // ADR 30.9.26s §2 — the fallback reads the console's list on `/admin`.
  const titleKey = useRouteTitleKey() ?? titleKeyFor(pathname, destinationsFor(pathname))
  const backTo = useRouteBackTo()
  const apps = useRouteApps()

  return (
    <div data-testid="app-header" className="border-b border-boundary bg-surface px-4">
      <TopBar
        title={titleKey === null ? '' : t(titleKey)}
        to={backTo ?? undefined}
        backLabel={t('nav_back')}
      >
        {apps === null ? null : (
          <Suspense fallback={null}>
            <HeaderApps apps={apps} />
          </Suspense>
        )}
        <InstallButton surface="header" />
        <HeaderSwitcher />
      </TopBar>
    </div>
  )
}
